// Ingredient Ordering — the "1A" engine. Enter units-to-produce per SKU (or load a booked run) →
// live BOM table (required lbs incl. overage, on-hand, to-order, cost) → generate a purchase order.
import { useMemo, useState } from "react";
import { computeRequirements } from "@shared/engine.ts";
import type { Order, OrderLine, RunItem } from "@shared/types.ts";
import { useData } from "../state/store.tsx";
import { id as newId } from "../lib/ids.ts";
import { StatCard, StatGrid, Badge, InfoTip, EmptyState } from "../components/ui.tsx";
import { fmtDate, mass, money, num } from "../lib/format.ts";

export default function Ordering() {
  const { data, mutate, saving } = useData();
  const [cans, setCans] = useState<Record<string, number>>({});
  const [runId, setRunId] = useState<string>("");
  const [done, setDone] = useState<string | null>(null);

  if (!data) return null;
  const cur = data.business.currency;
  const massUnit = data.business.massUnit || "lb";
  const unit = data.business.unitLabel;
  const activeSkus = data.skus.filter((s) => !s.discontinued && !s.assembled && !s.purchased);
  const openRuns = data.runs.filter((r) => !r.orderId && r.status !== "Delivered" && r.status !== "Cancelled");
  const orderedRuns = data.runs.filter((r) => r.orderId && r.status !== "Delivered" && r.status !== "Cancelled");

  const loadRun = (rid: string) => {
    setRunId(rid);
    setDone(null);
    if (!rid) { setCans({}); return; }
    const run = data.runs.find((r) => r.id === rid);
    if (!run) return;
    const next: Record<string, number> = {};
    for (const it of run.items) next[it.skuId] = it.cans;
    setCans(next);
  };

  const items: RunItem[] = useMemo(
    () => Object.entries(cans).map(([skuId, c]) => ({ skuId, cans: Number(c) || 0 })).filter((i) => i.cans > 0),
    [cans],
  );
  const rows = useMemo(() => computeRequirements(data, items), [data, items]);

  // If a run that's already been ordered is loaded, we TOP IT UP: order only the delta between the
  // current recipe and what its existing orders already cover — a newly added ingredient, or a raised
  // quantity. Otherwise this is a normal first order for the whole recipe.
  const loadedRun = runId ? data.runs.find((r) => r.id === runId) : undefined;
  const existingOrders = useMemo(
    () => (loadedRun ? data.orders.filter((o) => o.runId === loadedRun.id || o.id === loadedRun.orderId) : []),
    [data.orders, loadedRun],
  );
  const isTopUp = existingOrders.length > 0;
  const alreadyByIng = useMemo(() => {
    const m: Record<string, number> = {};
    for (const o of existingOrders) for (const l of o.lines) m[l.ingredient] = (m[l.ingredient] || 0) + l.toOrderLbs;
    return m;
  }, [existingOrders]);

  const r4 = (n: number) => Math.round(n * 1e4) / 1e4;
  const r2 = (n: number) => Math.round(n * 100) / 100;

  // What this action will actually order: the full requirement, or — on a top-up — just the still-
  // un-ordered delta per ingredient (current to-order minus what existing orders already cover).
  const displayRows = useMemo(() => {
    if (!isTopUp) return rows;
    return rows
      .map((r) => {
        const remaining = Math.max(0, r4(r.toOrderLbs - (alreadyByIng[r.ingredient] || 0)));
        return { ...r, toOrderLbs: remaining, orderUnits: r.packSize > 0 ? Math.round(remaining / r.packSize) : 0, netNeedLbs: remaining, excessLbs: 0, lineCost: r2(remaining * r.unitCost) };
      })
      .filter((r) => r.toOrderLbs > 0);
  }, [rows, isTopUp, alreadyByIng]);

  const toOrderRows = displayRows.filter((r) => r.toOrderLbs > 0);
  const total = displayRows.reduce((a, r) => a + r.lineCost, 0);
  const totalCans = items.reduce((a, i) => a + i.cans, 0);

  // The order lines to persist — canner-provided ingredients are never ordered by us.
  const orderLines: OrderLine[] = useMemo(
    () => displayRows.filter((r) => !r.provided && r.toOrderLbs > 0).map((r) => ({
      id: newId("ol"), ingredient: r.ingredient, supplier: r.supplier,
      requiredLbs: r4(r.requiredLbs), onHandLbs: r4(r.onHandLbs), netNeedLbs: r4(r.netNeedLbs),
      toOrderLbs: r4(r.toOrderLbs), orderUnits: r.orderUnits, unitCost: r4(r.unitCost), lineCost: r2(r.lineCost), received: null,
    })),
    [displayRows],
  );

  const byFlavour = useMemo(() => {
    const m = new Map<string, typeof activeSkus>();
    for (const s of activeSkus) { if (!m.has(s.flavour)) m.set(s.flavour, []); m.get(s.flavour)!.push(s); }
    return Array.from(m.entries());
  }, [activeSkus]);

  const generate = async () => {
    if (!orderLines.length) return;
    const order: Order = {
      id: newId("ord"),
      businessId: data.business.id,
      runId: runId || undefined,
      createdAt: new Date().toISOString(),
      status: "Ordered",
      lines: orderLines,
    };
    await mutate((d) => {
      d.orders.unshift(order);
      if (runId) {
        const run = d.runs.find((r) => r.id === runId);
        // Only the first order owns the run link; a top-up leaves it (and status) intact.
        if (run && !run.orderId) { run.orderId = order.id; if (run.status === "Booked") run.status = "Ingredients Ordered"; }
      }
    });
    setDone(order.id);
  };

  return (
    <div className="space-y-4">
      <div className="card space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold">Ingredient Ordering</h2>
            <p className="text-muted text-xs mt-0.5">Enter how many {unit}s to produce. The order is calculated from the recipes, plus a {(data.business.overagePct * 100).toFixed(0)}% overage, minus on-hand stock.</p>
          </div>
          {(openRuns.length > 0 || orderedRuns.length > 0) && (
            <label className="text-sm">
              <span className="label">Load a booked run</span>
              <select className="input w-auto py-1.5" value={runId} onChange={(e) => loadRun(e.target.value)}>
                <option value="">— free input —</option>
                {openRuns.length > 0 && (
                  <optgroup label="Booked — not yet ordered">
                    {openRuns.map((r) => <option key={r.id} value={r.id}>{new Date(r.scheduledDate).toLocaleDateString()} · {r.items.reduce((a, i) => a + i.cans, 0)} {unit}s</option>)}
                  </optgroup>
                )}
                {orderedRuns.length > 0 && (
                  <optgroup label="Already ordered — add to it">
                    {orderedRuns.map((r) => <option key={r.id} value={r.id}>{new Date(r.scheduledDate).toLocaleDateString()} · {r.items.reduce((a, i) => a + i.cans, 0)} {unit}s</option>)}
                  </optgroup>
                )}
              </select>
            </label>
          )}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {byFlavour.map(([flavour, skus]) => (
            <div key={flavour} className="rounded-lg border border-edge/70 p-3">
              <div className="text-sm font-medium mb-2">{flavour}</div>
              <div className="space-y-1.5">
                {skus.map((s) => (
                  <label key={s.id} className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted">{s.strength}</span>
                    <input type="number" min={0} step={100} className="input w-28 py-1 text-right" placeholder="0"
                      value={cans[s.id] ?? ""} onChange={(e) => setCans((c) => ({ ...c, [s.id]: Number(e.target.value) }))} />
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {isTopUp && items.length > 0 && (
        <div className="card border-brand/30 bg-brand/[0.04]">
          <div className="text-sm font-semibold">Adding to an existing order</div>
          <p className="text-muted text-xs mt-0.5">
            This run was already ordered{existingOrders.length ? ` on ${fmtDate(existingOrders[existingOrders.length - 1].createdAt)}` : ""}.{" "}
            {orderLines.length > 0
              ? `Only the ${orderLines.length} item${orderLines.length > 1 ? "s" : ""} below ${orderLines.length > 1 ? "are" : "is"} still un-ordered (added to the recipe or increased since) — generating sends just ${orderLines.length > 1 ? "those" : "that"} into Receiving.`
              : "Everything the recipe needs is already on an order — nothing left to add."}
          </p>
        </div>
      )}

      {items.length === 0 ? (
        <div className="card"><EmptyState title="Enter production quantities" sub={`Type the number of ${unit}s to produce for any SKU above to see the ingredient order.`} /></div>
      ) : (
        <>
          <StatGrid>
            <StatCard label={`Total ${unit}s`} value={num(totalCans)} />
            <StatCard label="Ingredients to order" value={num(toOrderRows.length)} sub={`of ${rows.length} in recipe`} />
            <StatCard label="Order cost" value={money(total, cur)} accent />
            <StatCard label="Overage" value={`+${(data.business.overagePct * 100).toFixed(0)}%`} />
          </StatGrid>

          <div className="card overflow-x-auto">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <h3 className="text-sm font-semibold">Bill of materials</h3>
              <div className="flex items-center gap-2">
                {done ? <Badge tone="ok">{isTopUp ? "Supplemental order created ✓" : "Order created ✓"}</Badge> : (
                  <button className="btn-primary btn-sm" disabled={saving || !orderLines.length} onClick={generate}>
                    {saving ? "…" : isTopUp ? `Generate supplemental order${orderLines.length ? ` · ${orderLines.length} item${orderLines.length > 1 ? "s" : ""}` : ""}` : "Generate order"}
                  </button>
                )}
              </div>
            </div>
            <table className="w-full min-w-[720px]">
              <thead><tr>
                <th className="th">Ingredient</th>
                <th className="th">Supplier</th>
                <th className="th text-right">Required <InfoTip text={`Recipe amount for the run × (1 + ${(data.business.overagePct * 100).toFixed(0)}% overage).`} /></th>
                <th className="th text-right">On hand</th>
                <th className="th text-right">Need</th>
                <th className="th text-right">To order <InfoTip text="Rounded up to whole purchase packs, honouring the minimum order quantity (set per ingredient in Admin). Excess over need shown below the amount." /></th>
                <th className="th text-right">$/{massUnit}</th>
                <th className="th text-right">Line cost</th>
              </tr></thead>
              <tbody>
                {displayRows.map((r) => (
                  <tr key={r.ingredient} className={r.toOrderLbs > 0 ? "" : "opacity-55"}>
                    <td className="td font-medium">{r.ingredient}</td>
                    <td className="td text-muted">{r.supplier || "—"}</td>
                    <td className="td text-right tabular-nums">{mass(r.requiredLbs, massUnit)}</td>
                    <td className="td text-right tabular-nums">{mass(r.onHandLbs, massUnit)}</td>
                    <td className="td text-right tabular-nums text-muted">{r.provided ? "—" : r.netNeedLbs > 0 ? mass(r.netNeedLbs, massUnit) : "—"}</td>
                    <td className="td text-right tabular-nums font-semibold">
                      {r.provided ? <span className="text-muted font-normal">canner-supplied</span>
                        : r.toOrderLbs > 0 ? (
                          <span>{mass(r.toOrderLbs, massUnit)}
                            {r.orderUnits > 0 && <span className="block text-[11px] font-normal text-muted">{r.orderUnits} × {r.packSize}{massUnit} pack{r.excessLbs > 0.001 ? ` · +${mass(r.excessLbs, massUnit)} excess` : ""}</span>}
                          </span>
                        ) : <span className="text-ok">covered</span>}
                    </td>
                    <td className="td text-right tabular-nums text-muted">{r.unitCost ? money(r.unitCost, cur) : "—"}</td>
                    <td className="td text-right tabular-nums">{r.lineCost ? money(r.lineCost, cur) : "—"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr>
                <td className="td font-semibold" colSpan={7}>Total</td>
                <td className="td text-right font-semibold tabular-nums">{money(total, cur)}</td>
              </tr></tfoot>
            </table>
            {done && <p className="text-xs text-muted mt-3">Order added — track it and confirm receipts under <span className="font-medium">Receiving</span>.</p>}
          </div>
        </>
      )}
    </div>
  );
}
